import { useState, useEffect } from "react";
import { AuthAPI } from "../../api/AuthAPI";

const STORAGE_KEY = "register_form_draft";

const FIELDS = [
  { name: "name",            label: "Full name",         placeholder: "Jane Smith",       type: "text"     },
  { name: "email",           label: "Email address",     placeholder: "you@example.com",  type: "email"    },
  { name: "phone",           label: "Phone (optional)",  placeholder: "98765 43210",      type: "tel"      },
  { name: "password",        label: "Password",          placeholder: "Min 6 characters", type: "password" },
  { name: "confirmPassword", label: "Confirm password",  placeholder: "Repeat password",  type: "password" },
];

const EMPTY = { name: "", email: "", phone: "", password: "", confirmPassword: "" };

/* ── per-field validators ── */
const validators = {
  name:            (v) => !v.trim()                        ? "Name is required"                      : "",
  email:           (v) => !v.trim()                        ? "Email is required"
                        : !/\S+@\S+\.\S+/.test(v)          ? "Enter a valid email address"           : "",
  phone:           (v) => v && !/^\d{10}$/.test(v)         ? "Enter a valid 10-digit number"         : "",
  password:        (v) => v.length < 6                     ? "At least 6 characters required"        : "",
  confirmPassword: (v, form) => v !== form.password        ? "Passwords do not match"                : "",
};

function validateAll(form) {
  const errs = {};
  FIELDS.forEach(({ name }) => {
    const msg = validators[name]?.(form[name], form) ?? "";
    if (msg) errs[name] = msg;
  });
  return errs;
}

export default function RegisterForm({ onSuccess }) {
  /* ── restore draft from sessionStorage ── */
  const [form, setForm] = useState(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // never restore passwords for security
        return { ...EMPTY, ...parsed, password: "", confirmPassword: "" };
      }
    } catch (_) {}
    return EMPTY;
  });

  const [errors,  setErrors]  = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState("");
  const [serverError, setServerError] = useState("");
  const [success, setSuccess] = useState(false);

  /* ── persist non-sensitive fields on every change ── */
  useEffect(() => {
    const { password, confirmPassword, ...safe } = form;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(safe));
  }, [form]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...form, [name]: value };
    setForm(updated);
    setServerError("");

    // live-validate if field was already touched
    if (touched[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: validators[name]?.(value, updated) ?? "",
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setFocused("");
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({
      ...prev,
      [name]: validators[name]?.(value, form) ?? "",
    }));
  };

  const submit = async () => {
    // mark all touched so every error shows
    const allTouched = Object.fromEntries(FIELDS.map(({ name }) => [name, true]));
    setTouched(allTouched);
    const errs = validateAll(form);
    setErrors(errs);
    if (Object.values(errs).some(Boolean)) return;

    try {
      setLoading(true);
      setServerError("");
      await AuthAPI.signup(form);
      setSuccess(true);
      sessionStorage.removeItem(STORAGE_KEY);
      setTimeout(() => onSuccess(), 1200);
    } catch (err) {
      setServerError(err.response?.data?.message || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div style={{ textAlign: "center", padding: "24px 0" }}>
        <div style={{
          width: 48, height: 48, borderRadius: "50%",
          background: "#dcfce7", display: "flex", alignItems: "center",
          justifyContent: "center", margin: "0 auto 12px",
        }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <p style={{ fontSize: "15px", fontWeight: 600, color: "#111827", margin: "0 0 4px" }}>Account created!</p>
        <p style={{ fontSize: "13px", color: "#6b7280" }}>Redirecting to sign in…</p>
      </div>
    );
  }

  return (
    <div>
      {FIELDS.map(({ name, label, placeholder, type }) => {
        const err = touched[name] && errors[name];
        const isPhone = name === "phone";
        return (
          <div key={name} style={{ marginBottom: "10px" }}>
            <label style={labelStyle}>
              {label}
              {isPhone && <span style={{ color: "#9ca3af", fontWeight: 400, marginLeft: 4 }}>(optional)</span>}
            </label>

            {/* phone gets +91 prefix */}
            {isPhone ? (
              <div style={{ display: "flex" }}>
                <span style={prefixStyle}>+91</span>
                <input
                  type="tel"
                  name={name}
                  placeholder={placeholder}
                  value={form[name]}
                  onChange={(e) => handleChange({ target: { name, value: e.target.value.replace(/\D/g, "").slice(0, 10) } })}
                  onFocus={() => setFocused(name)}
                  onBlur={handleBlur}
                  maxLength={10}
                  style={{
                    ...inputStyle(focused === name, !!err),
                    borderLeft: "none",
                    borderRadius: "0 10px 10px 0",
                    flex: 1,
                  }}
                />
              </div>
            ) : (
              <input
                type={type}
                name={name}
                placeholder={placeholder}
                value={form[name]}
                onChange={handleChange}
                onFocus={() => setFocused(name)}
                onBlur={handleBlur}
                style={inputStyle(focused === name, !!err)}
              />
            )}

            {err && (
              <p style={fieldErrorStyle}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ flexShrink: 0 }}>
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                {err}
              </p>
            )}
          </div>
        );
      })}

      {serverError && (
        <div style={{
          background: "#fef2f2", border: "1px solid #fecaca",
          borderRadius: "8px", padding: "10px 12px",
          fontSize: "13px", color: "#dc2626", marginTop: "4px",
          display: "flex", alignItems: "flex-start", gap: "8px",
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ flexShrink: 0, marginTop: 1 }}>
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          {serverError}
        </div>
      )}

      <button onClick={submit} disabled={loading} style={btnStyle(loading)}>
        {loading ? <><Spinner /> Creating account…</> : "Create account"}
      </button>
    </div>
  );
}

function Spinner() {
  return (
    <span style={{
      width: 14, height: 14,
      border: "2px solid rgba(255,255,255,0.35)",
      borderTopColor: "#fff", borderRadius: "50%",
      display: "inline-block",
      animation: "spin 0.7s linear infinite",
      marginRight: 8,
    }} />
  );
}

const labelStyle = {
  display: "block", fontSize: "12px", fontWeight: 500,
  color: "#374151", marginBottom: "5px", letterSpacing: "0.02em",
};

const prefixStyle = {
  height: "40px", padding: "0 11px",
  border: "1.5px solid #e5e7eb", borderRight: "none",
  borderRadius: "10px 0 0 10px",
  display: "flex", alignItems: "center",
  fontSize: "13.5px", color: "#6b7280",
  background: "#f3f4f6", userSelect: "none", flexShrink: 0,
};

const inputStyle = (focused, hasErr) => ({
  width: "100%", height: "40px", padding: "0 13px",
  border: `1.5px solid ${hasErr ? "#fca5a5" : focused ? "#2563eb" : "#e5e7eb"}`,
  borderRadius: "10px", fontSize: "13.5px", color: "#111827",
  outline: "none", boxSizing: "border-box",
  transition: "border-color 0.15s", background: hasErr ? "#fff8f8" : "#fafafa",
});

const fieldErrorStyle = {
  display: "flex", alignItems: "center", gap: "5px",
  fontSize: "11.5px", color: "#dc2626", marginTop: "5px",
};

const btnStyle = (loading) => ({
  width: "100%", height: "42px", marginTop: "14px",
  borderRadius: "10px", border: "none",
  background: loading ? "#93c5fd" : "#2563eb",
  color: "#fff", fontSize: "14px", fontWeight: 500,
  cursor: loading ? "not-allowed" : "pointer",
  transition: "background 0.15s", letterSpacing: "0.01em",
  display: "flex", alignItems: "center", justifyContent: "center",
});